/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cn } from '@/shared/ui/utils/tailwind';
import { StatusBadge, Text, UnstyledButton } from '@/shared/ui';
import type { LaunchBoardUnit } from '../types';
import {
  ALLOCATION_STATUS_CARD,
  ALLOCATION_STATUS_BADGE,
} from './launch-unit-status';

export interface LaunchUnitCardProps {
  unit: LaunchBoardUnit;
  selected?: boolean;
  statusLabel: string;
  countdownLabel?: string;
  onSelect: (unit: LaunchBoardUnit) => void;
}

/**
 * Formats optional list price for the unit card.
 *
 * @param price - List price or null.
 * @returns Display string or empty.
 */
export function formatUnitPrice(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return '';
  return price.toLocaleString(undefined, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

/**
 * Compact unit tile on the launch allocation board.
 *
 * @param props - Unit data and selection handler.
 * @returns Clickable unit card.
 */
export function LaunchUnitCard({
  unit,
  selected,
  statusLabel,
  countdownLabel,
  onSelect,
}: LaunchUnitCardProps) {
  const priceLabel = formatUnitPrice(unit.list_price);
  const meta = [unit.typology, priceLabel].filter(Boolean).join(' · ');
  const status = unit.allocation_status;

  return (
    <UnstyledButton
      type="button"
      data-testid={`launch-unit-${unit.unit_id}`}
      data-status={unit.allocation_status}
      onClick={() => onSelect(unit)}
      className={cn(
        'flex cursor-pointer flex-col gap-[var(--space-2)]',
        'rounded-(--radius-lg) border p-[var(--space-3)]',
        'bg-(--surface-primary) text-left',
        'shadow-[var(--shadow-sm)]',
        'hover:shadow-[var(--shadow-md)] hover:bg-(--surface-secondary)',
        'focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-2',
        'transition-[box-shadow,background-color,border-color] duration-(--duration-fast)',
        selected
          ? 'border-[var(--seed-base)] ring-2 ring-[var(--seed-base)]'
          : 'border-(--border-subtle)',
        ALLOCATION_STATUS_CARD[status],
      )}
    >
      <Text
        component="span"
        className="truncate text-[length:var(--text-body-sm)] font-semibold text-(--text-primary) leading-tight"
      >
        {unit.label}
      </Text>

      <div className="flex items-center gap-[var(--space-2)]">
        <StatusBadge className={ALLOCATION_STATUS_BADGE[status]}>
          {statusLabel}
        </StatusBadge>
        {countdownLabel ? (
          <Text
            component="span"
            data-testid={`launch-unit-countdown-${unit.unit_id}`}
            className="text-[length:var(--text-micro)] text-(--text-secondary) tabular-nums"
          >
            {countdownLabel}
          </Text>
        ) : null}
      </div>

      {meta ? (
        <Text
          component="span"
          data-testid={`launch-unit-meta-${unit.unit_id}`}
          className="truncate text-[length:var(--text-meta)] text-(--text-tertiary)"
        >
          {meta}
        </Text>
      ) : null}
    </UnstyledButton>
  );
}
